/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Settings > Real Estate: which kind of real estate business this is
 * (personas) and which country's rules it runs on (region, read only here;
 * the platform admin sets it).
 *
 * Personas only decide which Real Estate pages appear in navigation. Licences
 * still decide what the tenant can use, and an unlicensed persona cannot be
 * switched on. GET/PUT /re/settings (backend/app/real_estate/routes/settings.py).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Building2, Globe2, Handshake, KeyRound, Landmark, Lock } from "lucide-react";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PageHeader from "components/v2/PageHeader";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import { v2 as t } from "styles/tokens/v2";
import { getApiErrorMessage } from "utils/apiErrors";
import { validatePersonas } from "./personas";
import PortalSettingsCard from "./PortalSettingsCard";

const PERSONA_ICONS = {
  DEVELOPER: Building2,
  AGENCY: Handshake,
  PROPERTY_MANAGER: KeyRound,
  MORTGAGE_BROKER: Landmark,
};

const MODULE_NAMES = {
  RE_PROJECTS: "Projects & inventory",
  RE_SALES: "Sales & payment plans",
  RE_BROKERAGE: "Listings & brokerage",
  RE_LEASING: "Leasing",
  RE_MORTGAGE: "Mortgages",
};

function SectionCard({ title, summary, children }) {
  return (
    <Card sx={{ p: 3, mb: 3, border: `1px solid ${t.border}`, boxShadow: "none" }}>
      <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, color: t.text }}>
        {title}
      </Typography>
      {summary && (
        <Typography sx={{ fontSize: 14, color: t.text3, mt: 0.5, mb: 2 }}>{summary}</Typography>
      )}
      {children}
    </Card>
  );
}

function Fact({ label, value }) {
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography sx={{ fontSize: 12, color: t.text3, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text, mt: 0.25 }}>{value}</Typography>
    </Box>
  );
}

export default function RealEstateSettingsPage() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance, user } = useAuth();
  const { refetch, isModuleEnabled } = useModuleEntitlements();
  const navigate = useNavigate();
  const hasWebsite = isModuleEnabled?.("RE_WEBSITE");
  const hasPortal = isModuleEnabled?.("RE_CUSTOMER_PORTAL");
  const isSystemAdmin = String(user?.role || "").toLowerCase() === "system_admin";

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [chosen, setChosen] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data: body } = await axiosInstance.get("/re/settings");
      setData(body);
      setChosen(new Set(body.personas.filter((p) => p.enabled).map((p) => p.code)));
    } catch (err) {
      setError(getApiErrorMessage(err, "Real Estate settings could not be loaded."));
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const saved = useMemo(
    () => new Set((data?.personas || []).filter((p) => p.enabled).map((p) => p.code)),
    [data]
  );
  const dirty = data && (saved.size !== chosen.size || [...chosen].some((c) => !saved.has(c)));
  const check = data ? validatePersonas(chosen, data.personas) : { codes: [] };

  const toggle = (code) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const save = async () => {
    if (check.error) return;
    setSaving(true);
    setError("");
    try {
      const { data: body } = await axiosInstance.put("/re/settings", { personas: check.codes });
      setData(body);
      setChosen(new Set(body.personas.filter((p) => p.enabled).map((p) => p.code)));
      setToast("Real Estate settings saved");
      // Navigation follows the personas; reload the entitlement payload.
      refetch?.();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save the Real Estate settings."));
    } finally {
      setSaving(false);
    }
  };

  const pack = data?.variant_pack;
  const hiddenNames = (data?.hidden_modules || []).map((m) => MODULE_NAMES[m] || m);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 3 : 4} pb={6} px={isV2 ? 0 : 2}>
        {isV2 ? (
          <PageHeader title="Real Estate" summary="The kind of real estate business you run, and the country rules it follows" />
        ) : (
          <MDBox mb={3}>
            <MDTypography variant="h4" fontWeight="medium">Real Estate</MDTypography>
            <MDTypography variant="button" color="text">
              The kind of real estate business you run, and the country rules it follows
            </MDTypography>
          </MDBox>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {!data && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {data && (
          <>
            <SectionCard
              title="Region"
              summary="Fields, rules, currency and wording follow this country. It is set by your Pearl CRM provider; contact support to change it."
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Globe2 size={18} color={t.text2} aria-hidden />
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>{data.variant_name}</Typography>
                </Box>
                <Fact label="Currency" value={pack?.currency} />
                <Fact label="Tax" value={pack ? `${pack.tax_name} ${pack.tax_pct}%` : "-"} />
                <Fact label={pack?.region_label || "Region"} value={`${pack?.regions?.length || 0} available`} />
                <Fact label="Rent" value={pack?.rent_payment_model === "BOND" ? "Bond and periodic rent" : "Post-dated cheques"} />
              </Box>
            </SectionCard>

            <SectionCard
              title="Your business"
              summary="Choose every kind of business you run. Pages for the others are taken out of the navigation; your data and everyone's permissions stay as they are."
            >
              {!isSystemAdmin && (
                <Alert severity="info" sx={{ mb: 2 }}>Only a system administrator can change these.</Alert>
              )}
              <Box role="group" aria-label="Kinds of real estate business">
                {data.personas.map((persona, index) => {
                  const Icon = PERSONA_ICONS[persona.code] || Building2;
                  const disabled = !isSystemAdmin || !persona.licensed || saving;
                  const row = (
                    <Box
                      key={persona.code}
                      component="label"
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        py: 1.5,
                        cursor: disabled ? "default" : "pointer",
                        opacity: persona.licensed ? 1 : 0.6,
                      }}
                    >
                      <Checkbox
                        checked={chosen.has(persona.code) && persona.licensed}
                        onChange={() => toggle(persona.code)}
                        disabled={disabled}
                        inputProps={{ "aria-label": persona.label }}
                        sx={{ mt: -0.75 }}
                      />
                      <Box sx={{ color: t.text2, mt: 0.25 }}>
                        <Icon size={18} aria-hidden />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>{persona.label}</Typography>
                          {!persona.licensed && (
                            <Chip size="small" icon={<Lock size={12} />} label="Not in your plan" sx={{ height: 22 }} />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 13, color: t.text3, mt: 0.25 }}>{persona.description}</Typography>
                        <Typography sx={{ fontSize: 12, color: t.text3, mt: 0.5 }}>
                          {persona.modules.map((m) => MODULE_NAMES[m] || m).join(" · ")}
                        </Typography>
                      </Box>
                    </Box>
                  );
                  return (
                    <React.Fragment key={persona.code}>
                      {index > 0 && <Divider sx={{ my: 0 }} />}
                      {persona.licensed ? row : (
                        <Tooltip title="Ask your Pearl CRM provider to add this module" placement="top-start">
                          {row}
                        </Tooltip>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>

              {hiddenNames.length > 0 && (
                <Typography sx={{ fontSize: 13, color: t.text3, mt: 2 }}>
                  Hidden from navigation now: {hiddenNames.join(", ")}.
                </Typography>
              )}
              {dirty && check.error && (
                <Typography sx={{ fontSize: 13, color: "#B42318", mt: 2 }}>{check.error}</Typography>
              )}

              {isSystemAdmin && (
                <Box sx={{ display: "flex", gap: 1, mt: 2.5 }}>
                  <Button
                    variant="contained"
                    onClick={save}
                    disabled={!dirty || Boolean(check.error) || saving}
                    sx={{ color: "#FFFFFF !important" }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  {dirty && (
                    <Button variant="outlined" onClick={() => setChosen(new Set(saved))} disabled={saving}>
                      Discard
                    </Button>
                  )}
                </Box>
              )}
            </SectionCard>

            <SectionCard
              title="Website"
              summary="A public website for your listings: buy, rent and property pages, open homes, enquiries and appraisal requests"
            >
              {hasWebsite ? (
                <Button variant="outlined" onClick={() => navigate("/website")}>
                  Open website settings
                </Button>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: t.text3 }}>
                  <Lock size={16} aria-hidden="true" />
                  <Typography sx={{ fontSize: 14, color: t.text3 }}>
                    Ask your Pearl CRM provider to add the Real Estate Website add-on.
                  </Typography>
                </Box>
              )}
            </SectionCard>

            <SectionCard
              title="Customer portal"
              summary="Buyers and renters apply from your website, upload documents and follow their application. You review them under Applications."
            >
              {hasPortal ? (
                <PortalSettingsCard isSystemAdmin={isSystemAdmin} onSaved={setToast} />
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: t.text3 }}>
                  <Lock size={16} aria-hidden="true" />
                  <Typography sx={{ fontSize: 14, color: t.text3 }}>
                    Ask your Pearl CRM provider to add the Real Estate Customer Portal add-on.
                  </Typography>
                </Box>
              )}
            </SectionCard>
          </>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setToast("")} sx={{ width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
